import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, MenuItem, Select, FormControl, IconButton, Tooltip,
  Divider, List, ListItem, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import apiClient from '../api/client';

const ORG_ROLES = ['owner', 'admin', 'member'];

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [orgDialog, setOrgDialog] = useState({ open: false, data: null });
  const [orgForm, setOrgForm] = useState({ name: '', contact_email: '', phone: '', address: '' });
  const [membersDialog, setMembersDialog] = useState({ open: false, org: null, members: [] });
  const [addMemberForm, setAddMemberForm] = useState({ email: '', role: 'member' });
  const [memberError, setMemberError] = useState('');

  const loadOrgs = useCallback(async () => {
    const { data } = await apiClient.get('/admin/organizations');
    setOrgs(data.data);
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const openCreate = () => {
    setOrgForm({ name: '', contact_email: '', phone: '', address: '' });
    setOrgDialog({ open: true, data: null });
  };

  const openEdit = (o) => {
    setOrgForm({ name: o.name, contact_email: o.contact_email || '', phone: o.phone || '', address: o.address || '' });
    setOrgDialog({ open: true, data: o });
  };

  const saveOrg = async () => {
    if (orgDialog.data) {
      await apiClient.put(`/admin/organizations/${orgDialog.data.id}`, orgForm);
    } else {
      await apiClient.post('/admin/organizations', orgForm);
    }
    setOrgDialog({ open: false, data: null });
    loadOrgs();
  };

  const deleteOrg = async (id) => {
    if (!window.confirm('Delete this organization? All member associations will also be removed.')) return;
    await apiClient.delete(`/admin/organizations/${id}`);
    loadOrgs();
  };

  const openMembers = async (org) => {
    const { data } = await apiClient.get(`/admin/organizations/${org.id}/members`);
    setMembersDialog({ open: true, org, members: data.data });
    setAddMemberForm({ email: '', role: 'member' });
    setMemberError('');
  };

  const refreshMembers = async () => {
    const { data } = await apiClient.get(`/admin/organizations/${membersDialog.org.id}/members`);
    setMembersDialog(d => ({ ...d, members: data.data }));
    loadOrgs();
  };

  const addMember = async () => {
    setMemberError('');
    try {
      await apiClient.post(`/admin/organizations/${membersDialog.org.id}/members`, addMemberForm);
      setAddMemberForm({ email: '', role: 'member' });
      await refreshMembers();
    } catch (err) {
      setMemberError(err.response?.data?.error || 'Failed to add member');
    }
  };

  const updateMemberRole = async (memberId, role) => {
    await apiClient.put(`/admin/organizations/${membersDialog.org.id}/members/${memberId}`, { role });
    await refreshMembers();
  };

  const removeMember = async (memberId) => {
    await apiClient.delete(`/admin/organizations/${membersDialog.org.id}/members/${memberId}`);
    await refreshMembers();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Organizations</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>Add Organization</Button>
      </Box>

      <Paper sx={{ bgcolor: '#1e293b' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#94a3b8' }}>Name</TableCell>
              <TableCell sx={{ color: '#94a3b8' }}>Type</TableCell>
              <TableCell sx={{ color: '#94a3b8' }}>Contact</TableCell>
              <TableCell sx={{ color: '#94a3b8' }}>Members</TableCell>
              <TableCell sx={{ color: '#94a3b8' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orgs.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#64748b' }}>No organizations yet</TableCell></TableRow>
            )}
            {orgs.map(o => (
              <TableRow key={o.id} hover sx={{ '&:hover': { bgcolor: '#334155' } }}>
                <TableCell sx={{ color: '#f1f5f9' }}>{o.name}</TableCell>
                <TableCell>
                  <Chip label={o.is_personal ? 'Personal' : 'Organization'} size="small" variant="outlined" sx={{ color: '#94a3b8', borderColor: '#475569' }} />
                </TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{o.contact_email || '—'}</TableCell>
                <TableCell>
                  <Chip label={`${o.members?.length ?? 0} members`} size="small" sx={{ bgcolor: '#334155', color: '#94a3b8' }} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Manage members">
                    <IconButton size="small" onClick={() => openMembers(o)} sx={{ color: '#94a3b8' }}><PeopleIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {!o.is_personal && <>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(o)} sx={{ color: '#94a3b8' }}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => deleteOrg(o.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={orgDialog.open} onClose={() => setOrgDialog({ open: false, data: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{orgDialog.data ? 'Edit Organization' : 'New Organization'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Name" value={orgForm.name} onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))} required size="small" />
          <TextField label="Contact Email" value={orgForm.contact_email} onChange={e => setOrgForm(f => ({ ...f, contact_email: e.target.value }))} size="small" />
          <TextField label="Phone" value={orgForm.phone} onChange={e => setOrgForm(f => ({ ...f, phone: e.target.value }))} size="small" />
          <TextField label="Address" value={orgForm.address} onChange={e => setOrgForm(f => ({ ...f, address: e.target.value }))} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgDialog({ open: false, data: null })}>Cancel</Button>
          <Button onClick={saveOrg} variant="contained" disabled={!orgForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={membersDialog.open} onClose={() => setMembersDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>{membersDialog.org?.name} — Members</DialogTitle>
        <DialogContent>
          <List disablePadding>
            {membersDialog.members.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No members yet</Typography>
            )}
            {membersDialog.members.map((m, i) => (
              <React.Fragment key={m.id}>
                {i > 0 && <Divider />}
                <ListItem secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" variant="standard">
                      <Select value={m.role} onChange={e => updateMemberRole(m.id, e.target.value)} disableUnderline>
                        {ORG_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <IconButton size="small" color="error" onClick={() => removeMember(m.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }>
                  <ListItemText primary={`${m.user?.first_name} ${m.user?.last_name}`} secondary={m.user?.email} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" mb={1}>Add member</Typography>
          {memberError && <Typography variant="caption" color="error" display="block" mb={1}>{memberError}</Typography>}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Email" size="small" value={addMemberForm.email} onChange={e => setAddMemberForm(f => ({ ...f, email: e.target.value }))} sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select value={addMemberForm.role} onChange={e => setAddMemberForm(f => ({ ...f, role: e.target.value }))}>
                {ORG_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" size="small" onClick={addMember} disabled={!addMemberForm.email}>Add</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialog(d => ({ ...d, open: false }))}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
